import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpTrayIcon, CheckCircleIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../components/ui/toast';
import { describeApiError } from '../../data/http';
import { getMyRegionHint } from '../../data/me';
import {
  SLIDE_LIBRARY_COUNTRIES,
  SLIDE_LIBRARY_LANGUAGES,
  languageForCountry,
  listSlideLibraryItems,
  splitSlideLibraryDeck
} from '../../data/slideLibrary';
import type { SlideLibraryItem, SplitSlideDeckResult } from '../../data/types';

/** '' means "All" for every select below. */
const ALL = '';

function baseNameOf(fileName: string): string {
  return fileName.replace(/\.[^./]+$/, '');
}

/** Studio-only: shows what's in the Slide Library, and splits an uploaded deck into new slides. */
export function SlideLibraryPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SplitSlideDeckResult | null>(null);

  // Upload-time metadata: Country/Language default from the uploader's own Entra region, deck name from the file - both overridable.
  const [uploadCountry, setUploadCountry] = useState('');
  const [uploadLanguage, setUploadLanguage] = useState('');
  const [uploadDeckName, setUploadDeckName] = useState('');
  const [deckNameTouched, setDeckNameTouched] = useState(false);

  const [items, setItems] = useState<SlideLibraryItem[] | null>(null);
  const [deckNameOptions, setDeckNameOptions] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [descriptionSearch, setDescriptionSearch] = useState('');

  // Country/Language/Name/RCOfficial are forwarded to the Logic App as an OData $filter; description search stays local.
  const [filterCountry, setFilterCountry] = useState(ALL);
  const [filterLanguage, setFilterLanguage] = useState(ALL);
  const [filterDeckName, setFilterDeckName] = useState(ALL);
  const [filterRcOfficial, setFilterRcOfficial] = useState(ALL);

  useEffect(() => {
    void getMyRegionHint().then((hint) => {
      if (!hint.country) return;
      setUploadCountry((current) => current || hint.country!);
      setUploadLanguage((current) => current || languageForCountry(hint.country!));
    });
  }, []);

  const load = () => {
    listSlideLibraryItems({
      country: filterCountry || undefined,
      language: filterLanguage || undefined,
      deckName: filterDeckName || undefined,
      rcOfficial: filterRcOfficial === ALL ? undefined : filterRcOfficial === 'true'
    })
      .then((loaded) => {
        setItems(loaded);
        setLoadError(null);
      })
      .catch((err: unknown) => setLoadError(describeApiError(err, t('wizard.slides.unavailable'))));
  };

  // The Deck Name filter's own options come from an unfiltered baseline fetch, so choosing
  // a Country/Language doesn't shrink the list of decks the user can still pick from.
  const loadDeckNameOptions = () => {
    listSlideLibraryItems()
      .then((all) => setDeckNameOptions([...new Set(all.map((item) => item.deckName).filter(Boolean))].sort()))
      .catch(() => undefined);
  };

  useEffect(load, [t, filterCountry, filterLanguage, filterDeckName, filterRcOfficial]);
  useEffect(loadDeckNameOptions, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file || busy) return;

    setBusy(true);
    setResult(null);
    try {
      const split = await splitSlideLibraryDeck(file, {
        country: uploadCountry,
        language: uploadLanguage,
        deckName: uploadDeckName.trim() || baseNameOf(file.name)
      });
      setResult(split);
      setFile(null);
      setUploadDeckName('');
      setDeckNameTouched(false);
      if (split.uploadedCount > 0) {
        load();
        loadDeckNameOptions();
      }
    } catch (err) {
      toast({ title: describeApiError(err, t('studio.slideLibrary.invalidFile')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const needle = descriptionSearch.trim().toLowerCase();
  const visibleItems = (items ?? []).filter((item) => !needle || item.description.toLowerCase().includes(needle));

  const selectClass =
    'rounded-sm border border-input bg-surface px-2 py-1.5 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="grid size-11 shrink-0 place-items-center rounded bg-accent text-accent-foreground ring-1 ring-border">
          <RectangleStackIcon className="size-5" aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-normal text-foreground">
            {t('studio.slideLibrary.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('studio.slideLibrary.subtitle')}</p>
        </div>
      </div>

      <form onSubmit={(event) => void submit(event)} className="mt-6 rounded-lg border border-border bg-surface p-4">
        <h2 className="text-base font-semibold text-foreground">{t('studio.slideLibrary.uploadTitle')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('studio.slideLibrary.uploadLabel')}</p>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex-1 cursor-pointer rounded-sm border border-dashed border-input bg-surface px-4 py-3 text-sm text-muted-foreground transition hover:border-ring hover:text-foreground">
            <input
              type="file"
              accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={(event) => {
                const selected = event.target.files?.[0] ?? null;
                setFile(selected);
                if (selected && !deckNameTouched) setUploadDeckName(baseNameOf(selected.name));
              }}
              className="sr-only"
            />
            {file ? <span className="font-medium text-foreground">{file.name}</span> : t('studio.slideLibrary.chooseFile')}
          </label>

          <button
            type="submit"
            disabled={!file || busy}
            className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowUpTrayIcon className="size-4" aria-hidden="true" />
            {busy ? t('studio.slideLibrary.uploading') : t('studio.slideLibrary.uploadButton')}
          </button>
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className="block text-sm">
            <span className="font-medium text-foreground">{t('studio.slideLibrary.deckNameLabel')}</span>
            <input
              type="text"
              value={uploadDeckName}
              onChange={(event) => {
                setUploadDeckName(event.target.value);
                setDeckNameTouched(true);
              }}
              placeholder={t('studio.slideLibrary.deckNameLabel')}
              className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-foreground">{t('studio.slideLibrary.countryLabel')}</span>
            <select
              value={uploadCountry}
              onChange={(event) => setUploadCountry(event.target.value)}
              className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
            >
              <option value="">{t('studio.slideLibrary.chooseCountry')}</option>
              {SLIDE_LIBRARY_COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-foreground">{t('studio.slideLibrary.languageLabel')}</span>
            <select
              value={uploadLanguage}
              onChange={(event) => setUploadLanguage(event.target.value)}
              className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
            >
              <option value="">{t('studio.slideLibrary.chooseLanguage')}</option>
              {SLIDE_LIBRARY_LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </select>
          </label>
        </div>

        {result ? (
          <div
            className={`mt-4 rounded-sm border-l-4 px-4 py-3 text-sm ${
              result.failures.length > 0
                ? 'border-destructive bg-destructive-soft text-destructive'
                : 'border-primary bg-accent text-accent-foreground'
            }`}
          >
            <p className="flex items-center gap-2 font-medium">
              {result.failures.length === 0 ? <CheckCircleIcon className="size-4 shrink-0" aria-hidden="true" /> : null}
              {t('studio.slideLibrary.resultSummary', { uploaded: result.uploadedCount, total: result.slideCount })}
            </p>
            {result.failures.length > 0 ? (
              <>
                <p className="mt-1 font-medium">{t('studio.slideLibrary.resultFailures')}</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5">
                  {result.failures.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                <p className="mt-2 text-destructive/80">{t('studio.slideLibrary.resultFailuresHint')}</p>
              </>
            ) : null}
          </div>
        ) : null}
      </form>

      <section className="mt-8">
        <h2 className="text-base font-semibold text-foreground">{t('studio.slideLibrary.currentTitle')}</h2>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="block text-sm">
            <span className="sr-only">{t('studio.slideLibrary.filterCountry')}</span>
            <select value={filterCountry} onChange={(event) => setFilterCountry(event.target.value)} className={selectClass}>
              <option value={ALL}>{t('studio.slideLibrary.allCountries')}</option>
              {SLIDE_LIBRARY_COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="sr-only">{t('studio.slideLibrary.filterLanguage')}</span>
            <select value={filterLanguage} onChange={(event) => setFilterLanguage(event.target.value)} className={selectClass}>
              <option value={ALL}>{t('studio.slideLibrary.allLanguages')}</option>
              {SLIDE_LIBRARY_LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="sr-only">{t('studio.slideLibrary.filterName')}</span>
            <select value={filterDeckName} onChange={(event) => setFilterDeckName(event.target.value)} className={selectClass}>
              <option value={ALL}>{t('studio.slideLibrary.allNames')}</option>
              {deckNameOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="sr-only">{t('studio.slideLibrary.filterRcOfficial')}</span>
            <select value={filterRcOfficial} onChange={(event) => setFilterRcOfficial(event.target.value)} className={selectClass}>
              <option value={ALL}>{t('studio.slideLibrary.allRcOfficial')}</option>
              <option value="true">{t('studio.slideLibrary.rcOfficialYes')}</option>
              <option value="false">{t('studio.slideLibrary.rcOfficialNo')}</option>
            </select>
          </label>

          <label htmlFor="slide-library-search" className="block flex-1 text-sm">
            <span className="sr-only">{t('studio.slideLibrary.search')}</span>
            <input
              id="slide-library-search"
              type="search"
              value={descriptionSearch}
              onChange={(event) => setDescriptionSearch(event.target.value)}
              placeholder={t('studio.slideLibrary.searchPlaceholder')}
              className="w-full min-w-48 rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
            />
          </label>
        </div>

        {loadError ? (
          <p role="alert" className="mt-3 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
            {loadError}
          </p>
        ) : items === null ? (
          <p className="mt-3 text-sm text-muted-foreground" aria-live="polite">
            {t('wizard.slides.loading')}
          </p>
        ) : items.length === 0 ? (
          <div className="mt-3 rounded-lg border border-border bg-surface p-6">
            <div className="grid size-10 place-items-center rounded bg-accent text-accent-foreground">
              <RectangleStackIcon className="size-5" aria-hidden="true" />
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{t('wizard.slides.empty')}</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">{t('studio.slideLibrary.noMatches')}</p>
        ) : (
          <ul className="mt-3 grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {visibleItems.map((item) => {
              const tooltipLines = [
                item.deckName ? t('studio.slideLibrary.deckLabel', { deckName: item.deckName }) : null,
                item.uploadedByName || item.uploadedByEmail
                  ? t('studio.slideLibrary.uploadedByLabel', { name: item.uploadedByName || item.uploadedByEmail })
                  : null,
                item.description || t('studio.slideLibrary.descriptionPending')
              ].filter(Boolean);

              return (
                <li key={item.id} className="overflow-hidden rounded-lg border border-border bg-surface transition hover:border-ring">
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt=""
                      title={tooltipLines.join('\n')}
                      className="aspect-video w-full border-b border-border object-cover"
                    />
                  ) : (
                    <div className="grid aspect-video w-full place-items-center border-b border-border bg-muted">
                      <RectangleStackIcon className="size-6 text-muted-foreground" aria-hidden="true" />
                    </div>
                  )}
                  <p className="truncate px-3 py-2 text-xs font-medium text-foreground" title={item.name}>
                    {item.name}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}

